import { RowWrapper } from '@dealstack/frontend/shared/components/DynamicResponsibleTable/RowWrapper';
import { ButtonsRight } from '@dealstack/frontend/shared/components/fundament/Button/ButtonLayouts';
import {
	CheckedButtonSquare,
	CloseButtonSquare,
	DeleteButtonSquare,
	ThreeDotMenuIconButton,
} from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { Cell } from '@dealstack/frontend/shared/components/Table/DefaultTable/Cell';
import { Row } from '@dealstack/frontend/shared/components/Table/DefaultTable/Row';
import { useSecurityTypeTranslation } from '@dealstack/frontend/shared/translationControllers/useCommonTranslationsController';
import { memo, useEffect } from 'react';
import styled from 'styled-components';
import { Select, TextInput, Flex, Text as MantineText } from '@mantine/core';
import { CreatableSelect } from '@dealstack/frontend/shared/components/forms/CreatableSelect/CreatableSelect';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import {
	createPendingPerson,
	useLegalPersonSelect,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Loans/components/ManageLoanDrawer/useLegalPersonSelect';
import { useParams } from 'react-router-dom';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useLoanDefinitionRowController } from './useLoanDefinitionRowController';
import { LoanDefinitionType } from '@dealstack/shared/lib/generated/models/LoanDefinitionType';

interface Props {
	className?: string;
	fieldIndex: number;
	onRemove: () => void;
	resetEditOnDestroy?: boolean;
}

const LoanDefinitionRow = ({ fieldIndex, onRemove, className, resetEditOnDestroy }: Props) => {
	const { dealId } = useParams();
	const tCommonUi = useTranslationCommonUi();
	const { control, getValues, setValue } = useFormContext();

	const {
		fieldData,
		fieldKeys,
		isValid,
		onSaveButtonClicked,
		onCancelButtonClicked,
		onEditButtonClicked,
		stopEdit,
	} = useLoanDefinitionRowController(fieldIndex, onRemove);

	const getLoanDefinitionTypeTranslation = useSecurityTypeTranslation();

	const { personOptions, isLoadingPersons } = useLegalPersonSelect({
		dealId: dealId || '',
		pendingPerson:
			fieldData.funderIsNew && fieldData.funderId && fieldData.funderName
				? { id: fieldData.funderId, name: fieldData.funderName }
				: null,
	});

	const onCreateFunder = (query: string) => {
		const pending = createPendingPerson(query);

		setValue(fieldKeys.funderId, pending.id);
		setValue(fieldKeys.funderIsNew, true);
		setValue(fieldKeys.funderName, pending.name);

		return pending.id;
	};

	const clearPendingFunder = (nextFunderId: string | null) => {
		if (!getValues(fieldKeys.funderIsNew) || nextFunderId === getValues(fieldKeys.funderId)) {
			return;
		}

		setValue(fieldKeys.funderIsNew, false);
		setValue(fieldKeys.funderName, undefined);
	};

	const getFunderDisplayName = () => {
		if (!fieldData.funderId) {
			return '';
		}

		const funder = personOptions.find((p) => p.value === fieldData.funderId);
		return funder?.label || fieldData.funder || '';
	};

	useEffect(() => {
		return () => {
			if (!(resetEditOnDestroy && fieldData.isOnEdit)) {
				return;
			}

			stopEdit();
			if (fieldData.isOnAdd) {
				onRemove();
			}
		};
	}, [fieldData.isOnAdd, fieldData.isOnEdit, onRemove, resetEditOnDestroy, stopEdit]);

	return (
		<RowWrapper isInvalid={fieldData.isOnEdit && !isValid}>
			<Row className={`loan-definition-row ${className || ''}`}>
				{fieldData.isOnEdit ? (
					<>
						<Cell>
							<Controller
								name={fieldKeys.type}
								control={control}
								render={({ field }) => (
									<Select
										{...field}
										size="md"
										rightSection={
											<Icon
												variant="caret-down"
												size="lg"
												color={defaultTheme.v3.colors.dark[9]}
											/>
										}
										rightSectionPointerEvents="none"
										data={[
											{
												value: LoanDefinitionType.MANAGEMENT_LOAN,
												label: getLoanDefinitionTypeTranslation(
													LoanDefinitionType.MANAGEMENT_LOAN
												),
											},
											{
												value: LoanDefinitionType.DEFERRED_PAYMENT,
												label: getLoanDefinitionTypeTranslation(
													LoanDefinitionType.DEFERRED_PAYMENT
												),
											},
										]}
									/>
								)}
							/>
						</Cell>
						<Cell>
							<Controller
								name={fieldKeys.name}
								control={control}
								render={({ field, fieldState }) => (
									<TextInput
										{...field}
										size="md"
										placeholder={tCommonUi('loan_definition.name')}
										error={fieldState.error?.message}
									/>
								)}
							/>
						</Cell>
						<Cell>
							<Controller
								name={fieldKeys.funderId}
								control={control}
								render={({ field, fieldState }) => (
									<CreatableSelect
										value={field.value}
										onChange={(value) => {
											clearPendingFunder(value);
											field.onChange(value);
										}}
										onBlur={field.onBlur}
										size="md"
										placeholder={tCommonUi('loan_definition.funder')}
										data={personOptions}
										error={fieldState.error?.message}
										disabled={isLoadingPersons}
										shouldCreate={(query, data) => {
											if (!query) {
												return false;
											}
											const funder = data.find(
												(funder) => funder.label === query
											);
											return !funder;
										}}
										onCreate={onCreateFunder}
										getCreateLabel={(query) => (
											<Flex align="center" gap="0.5rem">
												<Icon variant="add" />
												<MantineText>
													{tCommonUi('loan_definition.create_new_lender')}
												</MantineText>
												<MantineText>"{query}"</MantineText>
											</Flex>
										)}
									/>
								)}
							/>
						</Cell>

						<ButtonsRight gap="sm">
							<CheckedButtonSquare type="button" onClick={onSaveButtonClicked} />
							<CloseButtonSquare type="button" onClick={onCancelButtonClicked} />
							<DeleteButtonSquare type="button" onClick={onRemove} />
						</ButtonsRight>
					</>
				) : (
					<>
						<Cell>
							<Text variant="p-sm">
								{getLoanDefinitionTypeTranslation(fieldData.type)}
							</Text>
						</Cell>

						<Cell>
							<Text variant="p-sm" elipsis>
								{fieldData.name}
							</Text>
						</Cell>

						<Cell>
							<Text variant="p-sm" elipsis>
								{getFunderDisplayName()}
							</Text>
						</Cell>

						<Cell align="right">
							{fieldData.locked ? null : (
								<ThreeDotMenuIconButton onClick={onEditButtonClicked} />
							)}
						</Cell>
					</>
				)}
			</Row>
		</RowWrapper>
	);
};

const Memoized = memo(LoanDefinitionRow);
const Styled = styled(Memoized)`
	.mantine-Select-root,
	.mantine-TextInput-root,
	.mantine-InputBase-root {
		width: 100%;
	}
`;

export { Styled as LoanDefinitionRow };
